import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setBookedBy } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectBookedBy, selectGuestContact } from "@/redux/slices/clientBooking/selectors";
import { profileSelector } from "@/redux/slices/profile/selectors";

/**
 * The name that answers "who placed this booking?" — the person operating the sheet, NEVER the
 * passenger the trip is for.
 *
 * In self-service that's the logged-in client; under operator/EA on-behalf-of it's the operator or
 * assistant, which is why it reads the LOGGED-IN profile rather than the scoped account (contrast
 * `useAccountHolder`, which resolves the person the booking is FOR). The guest flow is the one case
 * where the two coincide: a guest has no account, so their own contact details are the booker.
 *
 * Previously this preferred the selected passenger over the profile, so choosing a favourite or
 * adding a guest silently re-attributed the booking to them — on every surface, not just OBO. The
 * self-service default only looked right because "book for the account holder" is stored as `null`
 * and fell past that branch.
 */
export const useBookedByName = (): string => {
  const guestContact = useAppSelector(selectGuestContact);
  const profile = useAppSelector(profileSelector);

  return guestContact
    ? `${guestContact.firstName} ${guestContact.lastName}`.trim()
    : `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim();
};

/**
 * Prefills "Booked by" into the booking draft as soon as a name is derivable, so the Other Details
 * step is complete — and shows in the Booking Summary — without an explicit Proceed. Everything else
 * in Other Details is optional.
 *
 * Only seeds while empty, so a value the user typed in the panel is never overwritten. Selecting or
 * changing the passenger does not alter it — see `useBookedByName`.
 *
 * `enabled` gates the seed so the summary isn't pre-populated before the user has gone through the
 * Trip Details step — the caller passes `true` once that step has been entered. Defaults to `true`
 * so the hook's own unit test (and any always-on caller) is unchanged.
 */
export const useBookedByPrefill = (enabled = true) => {
  const dispatch = useAppDispatch();
  const bookedBy = useAppSelector(selectBookedBy);
  const name = useBookedByName();

  useEffect(() => {
    if (!enabled || bookedBy) return;
    if (name) dispatch(setBookedBy(name));
  }, [enabled, bookedBy, name, dispatch]);
};
